import React, { useEffect, useMemo, useState } from 'react';
import { get, query, ref, startAt, endAt, orderByKey } from 'firebase/database';
import { BarChart3 } from 'lucide-react';
import { rtdb } from '../../../firebase';

const RANGES = [
  { key: '7', label: '7 jours', days: 7 },
  { key: '30', label: '30 jours', days: 30 },
  { key: '90', label: '90 jours', days: 90 }
];

function ymdNDaysAgo(n) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - n);
  return d.toISOString().slice(0, 10);
}

function ymdToday() {
  return new Date().toISOString().slice(0, 10);
}

function levelLabel(v) {
  if (v === null || v === undefined) return '—';
  if (v < 0.7) return 'Vide';
  if (v < 1.5) return 'Moyen';
  return 'Blindé';
}

function formatDateShort(ymd) {
  return new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: 'short' }).format(
    new Date(`${ymd}T12:00:00Z`)
  );
}

function StatsPanel({ crousId }) {
  const [rangeKey, setRangeKey] = useState('7');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!crousId) return;
    const range = RANGES.find((r) => r.key === rangeKey);
    if (!range) return;
    setLoading(true);
    setError(null);
    const q = query(
      ref(rtdb, `stats/by_crous/${crousId}`),
      orderByKey(),
      startAt(ymdNDaysAgo(range.days)),
      endAt(ymdToday())
    );
    get(q)
      .then((snap) => {
        const out = [];
        snap.forEach((child) => {
          const v = child.val();
          if (v && v.date) out.push(v);
        });
        out.sort((a, b) => a.date.localeCompare(b.date));
        setRows(out);
      })
      .catch((err) => setError(err))
      .finally(() => setLoading(false));
  }, [crousId, rangeKey]);

  const summary = useMemo(() => {
    if (rows.length === 0) return null;
    const totalPings = rows.reduce((a, r) => a + (r.pingCount || 0), 0);
    const weighted = rows.reduce((a, r) => a + (r.avgLevel || 0) * (r.pingCount || 0), 0);
    const avg = totalPings > 0 ? weighted / totalPings : 0;
    const peak = rows.reduce(
      (best, r) => (r.peakLevel > (best?.peakLevel ?? -1) ? r : best),
      null
    );
    return { totalPings, avg, peak };
  }, [rows]);

  // Bars scaled to 2 (max level)
  const maxBar = 2;

  return (
    <div className="est-card">
      <div className="est-card-head">
        <div>
          <span className="est-kicker">Historique</span>
          <h2>Affluence observée</h2>
          <p className="est-muted">
            Données agrégées chaque nuit depuis les signalements de la communauté.
          </p>
        </div>
        <div className="est-range-picker">
          {RANGES.map((r) => (
            <button
              key={r.key}
              type="button"
              className={`est-chip ${rangeKey === r.key ? 'is-selected' : ''}`}
              onClick={() => setRangeKey(r.key)}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {loading && <div className="est-empty est-empty-small">Chargement…</div>}
      {error && <div className="est-empty est-empty-small">Erreur de chargement.</div>}

      {!loading && !error && rows.length === 0 && (
        <div className="est-empty est-empty-small">
          Aucune donnée agrégée pour cette période. L'agrégation tourne chaque nuit à 00h30
          (Paris). Les signalements du jour en cours apparaîtront demain.
        </div>
      )}

      {!loading && !error && rows.length > 0 && (
        <>
          <div className="est-kpis">
            <div className="est-kpi">
              <span>Affluence moyenne</span>
              <strong>{summary.avg.toFixed(2)}</strong>
              <small>{levelLabel(summary.avg)}</small>
            </div>
            <div className="est-kpi">
              <span>Signalements</span>
              <strong>{summary.totalPings.toLocaleString('fr-FR')}</strong>
              <small>sur la période</small>
            </div>
            <div className="est-kpi">
              <span>Jour le plus chargé</span>
              <strong>{summary.peak ? formatDateShort(summary.peak.date) : '—'}</strong>
              <small>
                {summary.peak ? `${summary.peak.peakLevel.toFixed(2)} à ${summary.peak.peakHour}h` : ''}
              </small>
            </div>
          </div>

          <div className="est-bars">
            {rows.map((r) => {
              const ratio = Math.min(1, (r.avgLevel || 0) / maxBar);
              return (
                <div key={r.date} className="est-bar" title={`${r.date} — ${r.avgLevel?.toFixed(2) ?? 0}`}>
                  <div className="est-bar-fill" style={{ height: `${ratio * 100}%` }} />
                  <span className="est-bar-label">{formatDateShort(r.date).replace(' ', '')}</span>
                </div>
              );
            })}
          </div>

          <div className="est-hint">
            <BarChart3 size={14} />
            Niveau 0 = vide, 1 = moyen, 2 = blindé. Barre = moyenne pondérée du jour.
          </div>
        </>
      )}
    </div>
  );
}

export default StatsPanel;
